import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AGE_GATE_TEST_IDS = {
  dialog: "age-gate-dialog",
  dob: "age-gate-dob-input",
  attest: "age-gate-attest-checkbox",
  submit: "age-gate-submit-button",
  cancel: "age-gate-cancel-button",
};

export default function AgeGateDialog({ open, onOpenChange, onVerified }) {
  const { refreshUser } = useAuth();
  const [dob, setDob] = useState("");
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!attest) {
      toast.error("Please attest you are 18 or older");
      return;
    }
    if (!dob) {
      toast.error("Enter your date of birth");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/verify-age", { dob, attest: true });
      await refreshUser();
      toast.success("Age verified");
      onOpenChange(false);
      onVerified?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={AGE_GATE_TEST_IDS.dialog}
        className="bg-[#12141c] border-[#222634] text-slate-100 max-w-md"
      >
        <DialogHeader>
          <p className="telesis-eyebrow">18+ Verification</p>
          <DialogTitle className="text-xl font-bold mt-1">
            Prove you are of age
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Telesis allows mature content. Confirm your date of birth before activating
            membership. We store only the date and the attestation timestamp — never a photo
            or document.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">
              Date of birth
            </Label>
            <Input
              data-testid={AGE_GATE_TEST_IDS.dob}
              type="date"
              required
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="mt-1 bg-[#0e1017] border-[#222634] text-slate-100"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid={AGE_GATE_TEST_IDS.attest}
              checked={attest}
              onCheckedChange={(v) => setAttest(!!v)}
              className="mt-0.5 border-[#3b4256] data-[state=checked]:bg-yellow-400 data-[state=checked]:text-black"
            />
            <span className="text-xs text-slate-300 leading-relaxed">
              I attest, under penalty of membership revocation, that I am 18 years or older
              and that the date of birth above is true.
            </span>
          </label>
          <DialogFooter className="gap-2">
            <Button
              data-testid={AGE_GATE_TEST_IDS.cancel}
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-slate-400 hover:text-slate-100"
            >
              Cancel
            </Button>
            <Button
              data-testid={AGE_GATE_TEST_IDS.submit}
              type="submit"
              disabled={busy || !attest || !dob}
              className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
            >
              {busy ? "Verifying…" : "Verify & continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
